import { ConfigService } from '..';

/**
 * Provide helper functions related to responsive UI needs.
 */
export class ResponsiveUiHelper {

    /**
     * Initialize vuetify breakpoints.
     * 
     * @param vuetifyRef - The Vuetify plugin object reference.
     */
    public static initializeBreakpoints(vuetifyRef: any) {
      // override Vuetify breakpoints thresholds if needed
      const configBreakpointThresholds: any | null = ConfigService.getBreakpointThresholds();
      if (configBreakpointThresholds) {
        if (configBreakpointThresholds.xs) { vuetifyRef.breakpoint.thresholds.xs = configBreakpointThresholds.xs; }
        if (configBreakpointThresholds.sm) { vuetifyRef.breakpoint.thresholds.sm = configBreakpointThresholds.sm; }
        if (configBreakpointThresholds.md) { vuetifyRef.breakpoint.thresholds.md = configBreakpointThresholds.md; }
        if (configBreakpointThresholds.lg) { vuetifyRef.breakpoint.thresholds.lg = configBreakpointThresholds.lg; } 
      }
    }

    /**
     * Returns TRUE when less than low resolution tablet display, FALSE otherwise.
     * 
     * @param vuetifyRef - The Vuetify plugin object reference.
     */
    public static isFullscreenDisplay(vuetifyRef: any): boolean {
      // TRUE when less than low resolution tablet display, FALSE otherwise
      return (window.innerWidth <= vuetifyRef.breakpoint.thresholds.md) ? true : false;
    }

    /**
     * Return the value of the vuetify's breakpoint which match with the current screen dimensions.
     * 
     * @param vuetifyRef - The Vuetify plugin object reference.
     */
    public static getCurrentAppliedBreakpointValue(vuetifyRef: any): number {
        switch (vuetifyRef.breakpoint.name) {
            case 'xs': 
              return vuetifyRef.breakpoint.thresholds.xs;
            case 'sm': 
              return vuetifyRef.breakpoint.thresholds.sm;
            case 'md': 
              return vuetifyRef.breakpoint.thresholds.md;
            default: 
              return vuetifyRef.breakpoint.thresholds.lg;
        }
    }

    /**
     * Replace theme key words by the configured Vuetify color.
     * 
     * @param vuetifyRef - The Vuetify plugin object reference
     * @param themeToApply - The theme to apply: LIGHT or DARK
     * @param stylesRules - The configured styles rules object to parse
     */
    public static setExplicitConfiguredThemeColors(vuetifyRef: any, themeToApply: string, stylesRules: any): any {
      Object.getOwnPropertyNames(stylesRules).forEach((prop: string) => 
                  stylesRules[prop] = ResponsiveUiHelper.replaceByExplicitColor(vuetifyRef, themeToApply, stylesRules[prop]));

      return stylesRules;
    }

    /**
     * Replace theme key words by the configured Vuetify color.
     * 
     * @param vuetifyRef - The Vuetify plugin object reference
     * @param themeToApply - The theme to apply: LIGHT or DARK
     * @param colorCode - The configured color to parse
     */
    public static replaceByExplicitColor(vuetifyRef: any, themeToApply: string, ruleValue: string): string {
      switch (ruleValue) {
        case 'primary': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.primary
                                            : vuetifyRef.theme.themes.dark.primary;
        case 'primary !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.primary} !important`
                                            : `${vuetifyRef.theme.themes.dark.primary} !important`;
        case 'secondary': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.secondary
                                            : vuetifyRef.theme.themes.dark.secondary;
        case 'secondary !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.secondary} !important`
                                            : `${vuetifyRef.theme.themes.dark.secondary} !important`;
        case 'tertiary': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.tertiary
                                            : vuetifyRef.theme.themes.dark.tertiary;
        case 'tertiary !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.tertiary} !important`
                                            : `${vuetifyRef.theme.themes.dark.tertiary} !important`;
        case 'accent': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.accent
                                            : vuetifyRef.theme.themes.dark.accent;
        case 'accent !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.accent} !important`
                                            : `${vuetifyRef.theme.themes.dark.accent} !important`;
        case 'warning': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.warning
                                            : vuetifyRef.theme.themes.dark.warning;
        case 'warning !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.warning} !important`
                                            : `${vuetifyRef.theme.themes.dark.warning} !important`;
        case 'error': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.error
                                            : vuetifyRef.theme.themes.dark.error;
        case 'error !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.error} !important`
                                            : `${vuetifyRef.theme.themes.dark.error} !important`;
        case 'info': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.info
                                            : vuetifyRef.theme.themes.dark.info;
        case 'info !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.info} !important`
                                            : `${vuetifyRef.theme.themes.dark.info} !important`;
        case 'base': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.base
                                            : vuetifyRef.theme.themes.dark.base;
        case 'base !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.base} !important`
                                            : `${vuetifyRef.theme.themes.dark.base} !important`;
        case 'main': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.main
                                            : vuetifyRef.theme.themes.dark.main;
        case 'main !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.main} !important`
                                            : `${vuetifyRef.theme.themes.dark.main} !important`;
        case 'disabled': 
          return (themeToApply === 'light') ? vuetifyRef.theme.themes.light.disabled
                                            : vuetifyRef.theme.themes.dark.disabled;
        case 'disabled !important': 
          return (themeToApply === 'light') ? `${vuetifyRef.theme.themes.light.disabled} !important`
                                            : `${vuetifyRef.theme.themes.dark.disabled} !important`;
        default:
          return ruleValue;
      }
    }

    /**
     * Hides chatbox integration element.
     */
    public static hideChatbox() {
      const injectionElemId: string | undefined = ConfigService.getIntegrationTagId();
      if (injectionElemId) {
        const botDiv: HTMLElement | null = document.getElementById(injectionElemId);
        if (botDiv && botDiv.style.display !== "none") {
          botDiv.style.display = "none";
        }
      }
    }

    /**
     * Displays chatbox integration element.
     */
    public static showChatbox() {
      const injectionElemId: string | undefined = ConfigService.getIntegrationTagId();
      if (injectionElemId) {
        const botDiv: HTMLElement | null = document.getElementById(injectionElemId);
        if (botDiv && botDiv.style.display === "none") {
          botDiv.style.display = "block";
        }
      }
    }
}
